const CENTER_POINT = [52.044577, 6.616053];
const DEFAULT_ZOOM = 8;
const DETAIL_ZOOM = 15;
const EARTH_RADIUS = 6371;
const PI_180 = Math.PI / 180;

/**
 * Class MemberController
 *
 * @author Bas Milius <bas@ideemedia.nl>
 * @since 2021-09-01
 */
class MemberController {

	#centerPoint;
	#element;
	#filter;
	#map;
	#points;

	get centerPoint() {
		return this.#centerPoint;
	}

	get element() {
		return this.#element;
	}

	get filter() {
		return this.#filter;
	}

	get map() {
		return this.#map;
	}

	get points() {
		return this.#points;
	}

	/**
	 * MemberController Constructor.
	 *
	 * @param {HTMLElement} element
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	constructor(element) {
		this.#centerPoint = CENTER_POINT;
		this.#element = element;
		this.#points = [];

		const mapElm = this.#element.querySelector(".member-map");
		const scriptElm = document.querySelector("#member-data");

		if (scriptElm) {
			this.#points = JSON.parse(scriptElm.textContent);
		}

		this.#map = new MapController(this, mapElm);
	}

	/**
	 * Initialize stuff.
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	initialize() {
		if ("geolocation" in navigator) {
			navigator.geolocation.getCurrentPosition(position => {
				this.#centerPoint = [
					position.coords.latitude,
					position.coords.longitude
				];
			});
		}
	}
}

/**
 * Class MapController
 *
 * @author Bas Milius <bas@ideemedia.nl>
 * @since 2021-09-01
 */
class MapController {

	#bounds;
	#controller;
	#element;
	#infoWindow;
	#map;
	#markers;

	get map() {
		return this.#map;
	}

	get markers() {
		return this.#markers;
	}

	get points() {
		return this.#controller.points;
	}

	/**
	 * MapController Constructor.
	 *
	 * @param {MemberController} controller
	 * @param {HTMLElement} element
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	constructor(controller, element) {
		this.#bounds = new google.maps.LatLngBounds();
		this.#controller = controller;
		this.#element = element;
		this.#infoWindow = new google.maps.InfoWindow();
		this.#markers = [];

		this.initialize();
	}

	/**
	 * Initializes our map.
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	initialize() {
		this.#map = new google.maps.Map(this.#element, {
			center: {
				lat: this.#controller.centerPoint[0],
				lng: this.#controller.centerPoint[1]
			},
			clickableIcons: false,
			disableDefaultUI: true,
			gestureHandling: "cooperative",
			maxZoom: 18,
			zoom: DEFAULT_ZOOM
		});

		for (let point of this.#controller.points) {
			const marker = new MapMarkerController(this, point);

			this.#bounds.extend(marker.position);
			this.#markers.push(marker);
		}

		this.#map.fitBounds(this.#bounds);

		const listener = google.maps.event.addListener(this.#map, "idle", () => {
			this.#map.setZoom(DEFAULT_ZOOM);
			google.maps.event.removeListener(listener);
		});
	}

	/**
	 * Fits the given bounds inside the map.
	 *
	 * @param {google.maps.LatLngBounds} bounds
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	fitBounds(bounds) {
		this.#map.fitBounds(bounds, 90);
	}

	/**
	 * Launches the info window with the given content at the given marker.
	 *
	 * @param {google.maps.Marker} marker
	 * @param {String} content
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	launchInfoWindow(marker, content) {
		this.#infoWindow.setContent(content);
		this.#infoWindow.open(this.#map, marker);
	}

	/**
	 * Pans to the given position.
	 *
	 * @param {google.maps.LatLng} position
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	panTo(position) {
		this.#map.panTo(position);
	}

	/**
	 * Pan zooms to the given point.
	 *
	 * @param {MapMarkerController} point
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	panZoomTo(point) {
		this.#map.panTo(point.position);

		if (this.#map.getZoom() >= DETAIL_ZOOM) {
			return;
		}

		const delta = DETAIL_ZOOM - this.#map.getZoom();

		if (delta <= 4) {
			this.#map.setZoom(DETAIL_ZOOM);
		} else {
			this.#map.setZoom(this.#map.getZoom() + Math.ceil(delta / 2));
			setTimeout(() => this.#map.setZoom(DETAIL_ZOOM), 900);
		}
	}

	/**
	 * Zooms to the given level.
	 *
	 * @param {Number} level
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	zoomTo(level) {
		this.#map.setZoom(level);
	}

}

/**
 * Class MapMarkerController
 *
 * @author Bas Milius <bas@ideemedia.nl>
 * @since 2021-09-01
 */
class MapMarkerController {

	#index;
	#mapController;
	#marker;
	#point;
	#position;

	get isVisible() {
		return this.#marker.getVisible();
	}

	set isVisible(is) {
		this.#marker.setVisible(is);
	}

	get latitude() {
		return this.#point.meta.lat;
	}

	get longitude() {
		return this.#point.meta.lng;
	}

	get marker() {
		return this.#marker;
	}

	get point() {
		return this.#point;
	}

	get position() {
		return this.#position;
	}

	/**
	 * MapMarkerController Constructor.
	 *
	 * @param {MapController} mapController
	 * @param {*} point
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	constructor(mapController, point) {
		this.#index = mapController.points.findIndex(p => p.id === point.id) + 1;
		this.#mapController = mapController;
		this.#point = point;
		this.#position = pointToLatLng(point);

		this.#marker = new google.maps.Marker({
			animation: google.maps.Animation.DROP,
			map: this.#mapController.map,
			optimized: true,
			position: this.#position,
			title: point.name,
			zIndex: this.#index,
			icon: {
				path: "M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z",
				fillColor: "#85B916",
				fillOpacity: 1,
				scale: .075,
			},
		});

		google.maps.event.addListener(this.#marker, "click", () => this.onClick());
		google.maps.event.addListener(this.#marker, "mouseover", () => this.onMouseOver());
		google.maps.event.addListener(this.#marker, "mouseout", () => this.onMouseOut());
	}

	/**
	 * Invoked when the marker is clicked.
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	onClick() {
		this.#mapController.launchInfoWindow(this.#marker, `${this.#point.name}`);
		// this.#mapController.panZoomTo(this);
	}

	/**
	 * Invoked when the marker is hovered.
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	onMouseOver() {
		this.#marker.setOptions({
			zIndex: this.#mapController.points.length + 2
		});
	}

	/**
	 * Invoked when the marker is not hovered anymore.
	 *
	 * @author Bas Milius <bas@ideemedia.nl>
	 * @since 2021-09-01
	 */
	onMouseOut() {
		this.#marker.setOptions({
			zIndex: this.#index
		});
	}

}

/**
 * Initializes the points of sale map.
 *
 * @author Bas Milius <bas@ideemedia.nl>
 * @since 2021-09-02
 */
export function initializeMemberMap() {
	const containerElm = document.querySelector(".member-overview");

	if (!containerElm) {
		return;
	}

	const controller = new MemberController(containerElm);
	controller.initialize();
}

/**
 * Calculates the distance between two points in kilometers.
 *
 * @param {{latitude: Number, longitude: Number}} point1
 * @param {{latitude: Number, longitude: Number}} point2
 * @param {Number} decimals
 * @returns {Number}
 *
 * @author Bas Milius <bas@ideemedia.nl>
 * @since 2021-09-02
 */
function distance({latitude: latitude1, longitude: longitude1}, {latitude: latitude2, longitude: longitude2}, decimals = 2) {
	const deltaLatitude = degreesToRadians(latitude2 - latitude1);
	const deltaLongitude = degreesToRadians(longitude2 - longitude1);
	const a = Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) + Math.cos(degreesToRadians(latitude1)) * Math.cos(degreesToRadians(latitude2)) * Math.sin(deltaLongitude / 2) * Math.sin(deltaLongitude / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

	const p = Math.pow(10, decimals);

	return Math.round((EARTH_RADIUS * c + Number.EPSILON) * p) / p;
}

/**
 * Converts the given degrees to radians.
 *
 * @param {Number} degrees
 * @returns {Number}
 *
 * @author Bas Milius <bas@ideemedia.nl>
 * @since 2021-09-02
 */
function degreesToRadians(degrees) {
	return degrees * PI_180;
}

/**
 * Converts the given point to a Google Maps LatLng instance.
 *
 * @param {*} point
 * @returns {google.maps.LatLng}
 *
 * @author Bas Milius <bas@ideemedia.nl>
 * @since 2021-09-02
 */
function pointToLatLng(point) {
	return new google.maps.LatLng(point.meta.lat, point.meta.lng);
}